import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  CheckCircle,
  Clock,
  Layers3,
  Shield,
  Sparkles,
  Target,
  Workflow,
  X,
} from 'lucide-react';
import { cadreBZSimplePiliers, cadreBZTransversales } from '../data/cadreBZMapping';

interface GuideCreationODSModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const principes = [
  {
    titre: 'Vendre un accompagnement',
    texte: "L’ODS présente une trajectoire de prise en charge, des résultats visés, une cadence et des responsabilités. Elle ne doit jamais ressembler à un catalogue de produits.",
  },
  {
    titre: 'Présenter trois choix réels',
    texte: 'Par défaut, le client compare trois niveaux d’accompagnement clairement différents par leur profondeur, leur cadence, leurs limites et leur investissement.',
  },
  {
    titre: 'Partir du contexte client',
    texte: 'Les faits, irritants, risques, ambitions et contraintes du client déterminent les options. Les produits et outils ne viennent qu’ensuite, comme moyens possibles.',
  },
  {
    titre: 'Rendre la décision défendable',
    texte: 'Chaque choix explique ce qui est couvert, ce qui ne l’est pas, les compromis, les conditions de réussite et les conséquences de la décision.',
  },
];

const trajectoires = [
  {
    numero: '01',
    nom: 'Essentielle',
    intention: 'Sécuriser l’essentiel',
    description: 'Une prise en charge ciblée sur la stabilité, les risques prioritaires et la visibilité minimale nécessaire.',
    couleur: 'border-slate-400/40 bg-slate-400/10 text-slate-200',
  },
  {
    numero: '02',
    nom: 'Évolution',
    intention: 'Optimiser et planifier',
    description: 'Une prise en charge proactive qui combine opérations, gouvernance, planification et améliorations prioritaires.',
    couleur: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200',
  },
  {
    numero: '03',
    nom: 'Transformation',
    intention: 'Transformer et accélérer',
    description: 'Une capacité accrue pour moderniser, automatiser et faire évoluer l’organisation avec une gouvernance renforcée.',
    couleur: 'border-violet-400/40 bg-violet-400/10 text-violet-200',
  },
];

const structureODS = [
  ['01', 'Promesse de valeur', 'Une couverture et une ouverture centrées sur la trajectoire du client, pas sur BZ ni sur une liste de solutions.'],
  ['02', 'Contexte client', 'Faits confirmés, irritants, risques, ambitions, contraintes et hypothèses explicitement identifiées.'],
  ['03', 'Cadre de valeur', 'Lecture du besoin selon les six piliers officiels du cadre client BZ et les deux dimensions transversales.'],
  ['04', 'Comparaison', 'Une vue synthèse des trois trajectoires : intention, profondeur, cadence, horizon, investissement et compromis.'],
  ['05', 'Détail des choix', 'Une page ou section par option avec accompagnement, livrables tangibles, limites, dépendances et conditions de réussite.'],
  ['06', 'Grille de portée', 'Une seule grille comparative des inclusions, exclusions, fréquences, volumes, responsabilités et éléments variables.'],
  ['07', 'Investissement', 'Prix et modalités justifiés par la capacité, l’effort, la cadence et la portée — jamais par un multiplicateur automatique.'],
  ['08', 'Mise en œuvre', 'Démarrage commun 0–30, 31–60 et 61–90 jours, puis trajectoire propre au choix retenu.'],
  ['09', 'Décision', 'Choix explicite du client, validations, conditions particulières, signatures et lien avec le contrat de service.'],
];

const controleQualite = [
  'Les informations client sont confirmées ou marquées « hypothèse à valider ».',
  'Les trois choix sont réellement distincts et aucun n’est artificiellement appauvri pour pousser une vente.',
  'Aucune option n’est automatiquement présélectionnée; toute recommandation BZ est expliquée par le contexte.',
  'Les six piliers sont considérés, même si seuls les piliers pertinents sont approfondis dans la portée.',
  'Les produits, licences et équipements sont présentés comme moyens ou coûts variables, pas comme la proposition de valeur.',
  'Chaque option expose ses limites, exclusions, dépendances, responsabilités et conditions de réussite.',
  'Les bénéfices sont formulés comme objectifs; aucune garantie de résultat n’est inventée.',
  'L’ODS finale précise qu’elle fait partie du contrat de service et prévaut en cas de divergence.',
];

export default function GuideCreationODSModal({ isOpen, onClose }: GuideCreationODSModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
          >
            <div className="max-h-[94vh] w-full max-w-7xl overflow-y-auto rounded-2xl border border-sky-400/20 bg-slate-950 text-white shadow-2xl">
              <header className="sticky top-0 z-10 border-b border-white/10 bg-slate-950/95 px-5 py-5 backdrop-blur md:px-8">
                <div className="flex items-start justify-between gap-5">
                  <div>
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">
                      <span>Ventes & Marketing</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                      <span>Opérations Commerciales</span>
                    </div>
                    <h2 className="text-2xl font-black tracking-tight md:text-4xl">Guide pour création d’ODS</h2>
                    <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-300 md:text-base">
                      Transformer les priorités d’affaires du client en choix d’accompagnement clairs, comparables et défendables.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                    aria-label="Fermer"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </header>

              <main className="space-y-8 p-5 md:p-8">
                <section className="relative overflow-hidden rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-sky-500/15 via-cyan-500/10 to-violet-500/10 p-6 md:p-8">
                  <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-cyan-400/10 blur-3xl" />
                  <div className="relative grid gap-8 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
                    <div>
                      <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-cyan-200">
                        <Sparkles className="h-4 w-4" />
                        Esprit Boréale V6
                      </div>
                      <h3 className="max-w-3xl text-3xl font-black leading-tight md:text-5xl">
                        Une décision claire,
                        <span className="block text-cyan-300">sans complexité inutile.</span>
                      </h3>
                      <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-300 md:text-lg">
                        Le rôle de BZ n’est pas de vendre une liste de produits. Il est de comprendre, comparer, faire choisir et accompagner l’évolution du client.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {['Comprendre', 'Comparer', 'Choisir', 'Évoluer'].map((etape, index) => (
                        <div key={etape} className="rounded-xl border border-white/10 bg-white/5 p-4">
                          <div className="text-xs font-black text-cyan-300">0{index + 1}</div>
                          <div className="mt-1 font-bold">{etape}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section>
                  <div className="mb-5 flex items-center gap-3">
                    <Target className="h-6 w-6 text-sky-300" />
                    <div>
                      <h3 className="text-2xl font-black">Principes non négociables</h3>
                      <p className="text-sm text-slate-400">La proposition de valeur précède toujours les moyens technologiques.</p>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {principes.map((principe, index) => (
                      <div key={principe.titre} className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
                        <div className="mb-3 flex items-center gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-400/15 text-sm font-black text-sky-300">{index + 1}</span>
                          <h4 className="font-bold text-white">{principe.titre}</h4>
                        </div>
                        <p className="text-sm leading-relaxed text-slate-300">{principe.texte}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-5 md:p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="mt-0.5 h-6 w-6 flex-none text-amber-300" />
                    <div>
                      <h3 className="text-xl font-black text-amber-100">Exception : demande d’un produit précis</h3>
                      <p className="mt-2 text-sm leading-relaxed text-amber-50/80">
                        Lorsqu’un client demande explicitement un produit, une licence, un équipement ou une intervention précise, il n’est pas nécessaire de fabriquer trois offres artificielles. L’ODS peut présenter un seul choix, tout en précisant le besoin, la portée d’accompagnement BZ, les services professionnels, les dépendances, les exclusions, les coûts variables et les conditions de succès.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <div className="mb-5 flex items-center gap-3">
                    <Layers3 className="h-6 w-6 text-fuchsia-300" />
                    <div>
                      <h3 className="text-2xl font-black">Les six piliers officiels</h3>
                      <p className="text-sm text-slate-400">Source de vérité : « Ébauche de cadre client BZ » dans Cadre référentiel.</p>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {cadreBZSimplePiliers.map((pilier, index) => (
                      <div key={pilier.id} className="group rounded-xl border border-fuchsia-400/15 bg-gradient-to-br from-fuchsia-400/[0.08] to-transparent p-5">
                        <div className="mb-3 flex items-center gap-3">
                          <span className="text-2xl font-black text-fuchsia-300/70">0{index + 1}</span>
                          <h4 className="font-bold leading-tight">{pilier.nom}</h4>
                        </div>
                        <p className="text-sm leading-relaxed text-slate-400">{pilier.description}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {cadreBZTransversales.map((dimension) => (
                      <div key={dimension.id} className="rounded-xl border border-emerald-400/15 bg-emerald-400/[0.07] p-4">
                        <div className="font-bold text-emerald-200">Dimension transversale — {dimension.nom}</div>
                        <p className="mt-1 text-sm text-slate-400">{dimension.description}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="mb-5 flex items-center gap-3">
                    <Briefcase className="h-6 w-6 text-emerald-300" />
                    <div>
                      <h3 className="text-2xl font-black">Trois trajectoires d’accompagnement</h3>
                      <p className="text-sm text-slate-400">Les noms peuvent être adaptés au client; la différence de valeur doit rester concrète.</p>
                    </div>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-3">
                    {trajectoires.map((option) => (
                      <div key={option.numero} className={`rounded-2xl border p-5 ${option.couleur}`}>
                        <div className="text-4xl font-black opacity-60">{option.numero}</div>
                        <div className="mt-4 text-xs font-black uppercase tracking-[0.17em] opacity-80">{option.nom}</div>
                        <h4 className="mt-1 text-xl font-black text-white">{option.intention}</h4>
                        <p className="mt-3 text-sm leading-relaxed text-slate-300">{option.description}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] p-5 text-sm leading-relaxed text-slate-300">
                    <strong className="text-white">Règle de conception :</strong> chaque trajectoire doit préciser l’intention, les piliers couverts, la profondeur, la cadence, les tangibles, l’horizon, l’investissement, les limites et les conditions de réussite. Les prix doivent découler de la portée et de la capacité requise — jamais d’un simple calcul automatique 75 % / 100 % / 150 %.
                  </div>
                </section>

                <section>
                  <div className="mb-5 flex items-center gap-3">
                    <Workflow className="h-6 w-6 text-cyan-300" />
                    <div>
                      <h3 className="text-2xl font-black">Architecture recommandée de l’ODS</h3>
                      <p className="text-sm text-slate-400">Inspirée de Boréale V6, normalisée avec le cadre client BZ à six piliers.</p>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    {structureODS.map(([numero, titre, description]) => (
                      <div key={numero} className="grid gap-2 border-b border-white/10 bg-white/[0.03] p-4 last:border-b-0 md:grid-cols-[60px_220px_1fr] md:items-start">
                        <div className="text-sm font-black text-cyan-300">{numero}</div>
                        <div className="font-bold text-white">{titre}</div>
                        <div className="text-sm leading-relaxed text-slate-400">{description}</div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="grid gap-5 lg:grid-cols-[1fr_.85fr]">
                  <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.07] p-5 md:p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <Shield className="h-6 w-6 text-emerald-300" />
                      <h3 className="text-xl font-black">Contrôle qualité avant émission</h3>
                    </div>
                    <div className="space-y-3">
                      {controleQualite.map((controle) => (
                        <div key={controle} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                          <CheckCircle className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
                          <span>{controle}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div className="rounded-2xl border border-sky-400/20 bg-sky-400/[0.07] p-5">
                      <div className="mb-3 flex items-center gap-3">
                        <Clock className="h-5 w-5 text-sky-300" />
                        <h3 className="font-black">Démarrage commun</h3>
                      </div>
                      <p className="text-sm leading-relaxed text-slate-300">
                        Les trois choix peuvent commencer par une discipline commune de prise en charge : 0–30 jours pour comprendre, 31–60 pour stabiliser, 61–90 pour planifier, puis 90+ pour exécuter la trajectoire retenue.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-violet-400/20 bg-violet-400/[0.07] p-5">
                      <div className="mb-3 flex items-center gap-3">
                        <Sparkles className="h-5 w-5 text-violet-300" />
                        <h3 className="font-black">Sources de référence</h3>
                      </div>
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li>• ODS test — Atelier Boréal — Version 6</li>
                        <li>• Ébauche de cadre client BZ — Cadre référentiel</li>
                        <li>• Contrat de service BZ et grille des coûts en vigueur</li>
                      </ul>
                      <p className="mt-4 text-xs leading-relaxed text-slate-500">
                        Les données, prix et hypothèses fictifs de Boréale servent uniquement d’exemple de structure; ils ne deviennent jamais des valeurs par défaut.
                      </p>
                    </div>
                  </div>
                </section>
              </main>

              <footer className="sticky bottom-0 flex justify-end border-t border-white/10 bg-slate-950/95 px-5 py-4 backdrop-blur md:px-8">
                <button onClick={onClose} className="rounded-lg bg-sky-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-sky-400">
                  Fermer le guide
                </button>
              </footer>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
